/**
 * Redirect generic `/doc/:doctype/...` routes to the canonical (mapped) route,
 * except the `/doc/:doctype/name/:name` disambiguation route and `?generic=1`.
 *
 * Extracted from StandardDetailPage (file-size split, behaviour unchanged).
 */

import { useEffect } from "react";
import type { Location, NavigateFunction } from "react-router-dom";
import {
  buildDetailRoute,
  buildNewRoute,
  isMappedDoctype,
} from "@/lib/config/docRoutes";

export function useCanonicalDocRedirect({
  doctype,
  docName,
  isNew,
  location,
  searchParams,
  navigate,
}: {
  doctype: string;
  docName: string;
  isNew: boolean;
  location: Location;
  searchParams: URLSearchParams;
  navigate: NavigateFunction;
}) {
  useEffect(() => {
    if (!doctype) return;

    // Don't redirect disambiguation route `/doc/:doctype/name/:name`
    const isDisambiguationRoute = location.pathname.startsWith(
      `/doc/${encodeURIComponent(doctype)}/name/`
    );
    if (isDisambiguationRoute) return;

    // Don't redirect when opened from panel fullscreen mode — keep generic view
    if (searchParams.get("generic") === "1") return;

    if (!isMappedDoctype(doctype)) return;

    const targetPath = isNew
      ? buildNewRoute(doctype)
      : buildDetailRoute(doctype, docName);

    const target = `${targetPath}${location.search || ""}`;
    const current = `${location.pathname}${location.search || ""}`;

    if (target !== current) {
      navigate(target, { replace: true });
    }
  }, [doctype, docName, isNew, location.pathname, location.search, navigate]);
}
